import type { DialogueContent } from '../../interactions/dialogue';
import type { InspectContent } from '../../interactions/inspect';
import type { SceneDefinition } from '../scene.types';

export const hallInspections: Record<string, InspectContent> = {
  'hall.poster': {
    title: 'Affiche du hall',
    imageUrl: '/props/prop-affiche.png',
    imageAlt: 'Affiche publicitaire de la boutique',
    lines: [
      'GRANDE PROMOTION sur les objets que votre chien a déjà décidé d’emporter.',
      'Offre valable jusqu’à ce qu’il change d’avis, c’est-à-dire immédiatement.',
      'Aucun chien n’a participé à l’inventaire. C’était plus prudent.',
    ],
  },
};

export const hallDialogues: DialogueContent[] = [
  {
    id: 'hall.entry',
    lines: [
      'Bienvenue chez Chien Heureux. Merci de garder votre chien près de vous. Enfin, essayez.',
    ],
    character: 'La boutique',
    once: true,
  },
  {
    id: 'hall.door',
    lines: [
      'La clochette sonne. Trois chiens du quartier lèvent la tête en même temps.',
      'Merci de ne pas lécher la vitrine.',
      'La porte grince depuis 2019. Nous appelons cela du caractère.',
    ],
  },
  {
    id: 'hall.mat',
    lines: [
      'Le tapis cache surtout du sable, deux croquettes et un mystère.',
      'Ce tapis a connu des choses.',
    ],
  },
  {
    id: 'hall.poster',
    lines: [
      'Affiche : « Grande promotion sur les objets que votre chien a déjà décidé d’emporter. »',
      'Affiche : « Aucun chien n’a participé à l’inventaire. C’était plus prudent. »',
    ],
  },
  {
    id: 'hall.bell',
    lines: [
      'Ding.',
      'Ding. Toujours personne.',
      'Le responsable du rayon est parti poursuivre un pigeon.',
    ],
  },
  {
    id: 'hall.dog',
    lines: ['Un chien traverse la pièce avec un air très occupé. Personne ne l’arrête.'],
  },
  {
    id: 'hall.pawprints',
    lines: [
      'Des traces de pattes. Elles vont dans trois directions à la fois.',
      'Celle-ci est fraîche. Enfin, fraîche pour une trace de patte.',
      'Quelqu’un a marché ici avec beaucoup de conviction.',
    ],
  },
];

export const hallScene: SceneDefinition = {
  id: 'hall',
  title: 'Le Hall',
  background: '/scenes/scene-hall-background-v2.webp',
  entryDialogueId: 'hall.entry',
  layers: [
    { id: 'hall-affiche', src: '/props/prop-affiche.png', depth: 0.2, alt: 'Affiche encadrée au mur du hall', x: 16, y: 13, width: 12 },
    { id: 'hall-tapis', src: '/props/prop-tapis.png', depth: 0.6, decorative: true, x: 36, y: 60, width: 27 },
    { id: 'hall-sonnette', src: '/props/prop-sonnette.png', depth: 0.5, decorative: true, x: 19, y: 45, width: 6 },
    { id: 'hall-chien', src: '/props/prop-chien.png', depth: 0.8, decorative: true, x: 70, y: 58, width: 12 },
  ],
  hotspots: [
    {
      id: 'hall.door',
      label: 'Faire sonner la clochette de la porte d’entrée',
      action: 'dialogue',
      target: 'hall.door',
      x: 41,
      y: 8,
      width: 18,
      height: 52,
      hint: 'La porte',
    },
    {
      id: 'hall.mat',
      label: 'Soulever le tapis d’entrée',
      action: 'collectible',
      target: 'bone-hall',
      x: 36,
      y: 62,
      width: 27,
      height: 10,
      hint: 'Le tapis',
    },
    {
      id: 'hall.poster',
      label: 'Examiner l’affiche murale',
      action: 'inspect',
      target: 'hall.poster',
      x: 16,
      y: 13,
      width: 12,
      height: 30,
      hint: 'L’affiche',
    },
    {
      id: 'hall.bell',
      label: 'Appuyer sur la sonnette du comptoir',
      action: 'animation',
      target: 'hall.bell',
      x: 19,
      y: 48,
      width: 8,
      height: 10,
      hint: 'La sonnette',
    },
    {
      id: 'hall.paw-1',
      label: 'Observer la première trace de pattes',
      action: 'animation',
      target: 'hall.pawprints',
      x: 30,
      y: 84,
      width: 7,
      height: 8,
      hint: 'Une trace',
    },
    {
      id: 'hall.paw-2',
      label: 'Observer la deuxième trace de pattes',
      action: 'animation',
      target: 'hall.pawprints',
      x: 44,
      y: 88,
      width: 7,
      height: 8,
      hint: 'Une trace',
    },
    {
      id: 'hall.paw-3',
      label: 'Observer la troisième trace de pattes',
      action: 'animation',
      target: 'hall.pawprints',
      x: 58,
      y: 84,
      width: 7,
      height: 8,
      hint: 'Une trace',
    },
    {
      id: 'hall.to-alimentation',
      label: 'Aller au rayon Alimentation',
      action: 'navigate',
      target: 'alimentation',
      x: 29,
      y: 5,
      width: 8,
      height: 53,
      hint: 'Rayon Alimentation',
      simplifiedLabel: 'Aller au rayon Alimentation',
    },
    {
      id: 'hall.to-friandises',
      label: 'Aller au rayon Friandises et mastications',
      action: 'navigate',
      target: 'friandises',
      x: 63,
      y: 5,
      width: 8,
      height: 53,
      hint: 'Rayon Friandises',
      simplifiedLabel: 'Aller au rayon Friandises',
    },
    {
      id: 'hall.to-comptoir',
      label: 'Aller au Comptoir',
      action: 'navigate',
      target: 'comptoir',
      x: 66,
      y: 55,
      width: 16,
      height: 22,
      hint: 'Le Comptoir',
      simplifiedLabel: 'Aller au Comptoir',
    },
  ],
};
